import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  Grid,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalBar as WineIcon,
  PhotoCamera as PhotoIcon,
  Euro as EuroIcon,
  ExpandMore as ExpandMoreIcon,
  Store as WineryIcon,
  BugReport as DebugIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  firebaseWinesService, 
  firebaseCategoriesService, 
  firebaseWineriesService, 
  firebaseTypesService
} from '../../services/firebaseService';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BackButton from '../../components/common/BackButton';

const AdminWines = () => {
  const [open, setOpen] = useState(false);
  const [editingWine, setEditingWine] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    wineryId: '',
    type: '',
    year: '',
    alcoholContent: '',
    stock: '',
    weightKg: '',
    image: null
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, wine: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [hierarchyKey, setHierarchyKey] = useState(0); // Para forzar re-render
  const [stockDialog, setStockDialog] = useState({ open: false, tab: 0 }); // 0: sin stock, 1: bajo stock
  const fileInputRef = useRef();

  const queryClient = useQueryClient();

  // Query para obtener vinos
  const { data: wines = [], isLoading, error } = useQuery({
    queryKey: ['firebaseWines'],
    queryFn: async () => {
      try {
        const response = await firebaseWinesService.getAll();
        return response.data.data;
      } catch (error) {
        console.error('❌ Error al obtener vinos de Firebase:', error);
        return [];
      }
    }
  });

  // Query para obtener categorías
  const { data: categories = [] } = useQuery({
    queryKey: ['firebaseCategories'],
    queryFn: async () => {
      try {
        const response = await firebaseCategoriesService.getAll();
        return response.data.data;
      } catch (error) {
        console.error('❌ Error al obtener categorías de Firebase:', error);
        return [];
      }
    }
  });

  // Query para obtener bodegas
  const { data: wineries = [] } = useQuery({
    queryKey: ['firebaseWineries'],
    queryFn: async () => {
      try {
        const response = await firebaseWineriesService.getAll();
        return response;
      } catch (error) {
        console.error('❌ Error al obtener bodegas de Firebase:', error);
        return [];
      }
    }
  });

  // Query para obtener tipos
  const { data: types = [] } = useQuery({
    queryKey: ['firebaseTypes'],
    queryFn: async () => {
      try {
        const response = await firebaseTypesService.getAll();
        return response;
      } catch (error) {
        console.error('❌ Error al obtener tipos de Firebase:', error);
        return [];
      }
    }
  });

  // Forzar recalculo de jerarquía cuando cambien los datos
  useEffect(() => {
    setHierarchyKey(prev => prev + 1);
  }, [wines.length, wineries.length, categories.length, types.length]);

  // Mutation para crear vino
  const createMutation = useMutation({
    mutationFn: async (formData) => {
      try {
        const result = await firebaseWinesService.create(formData);
        return result;
      } catch (error) {
        console.error('❌ Error al crear vino en Firebase:', error);
        return null;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseWines']);
      setOpen(false);
      resetForm();
      toast.success('Vino creado exitosamente en Firebase');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear el vino');
    }
  });

  // Mutation para actualizar vino
  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }) => {
      try {
        const result = await firebaseWinesService.update(id, formData);
        return result;
      } catch (error) {
        console.error('❌ Error al actualizar vino en Firebase:', error);
        return null;
      }
    },
    onSuccess: async () => {
      try {
        await Promise.all([
          queryClient.refetchQueries(['firebaseWines']),
          queryClient.refetchQueries(['firebaseWineries']),
          queryClient.refetchQueries(['firebaseCategories']),
          queryClient.refetchQueries(['firebaseTypes'])
        ]);
        
        // Actualizar la key de jerarquía para forzar re-render
        setHierarchyKey(prev => prev + 1);
        
        setOpen(false);
        resetForm();
        toast.success('Vino actualizado exitosamente en Firebase');
      } catch (error) {
        console.error('❌ Error al actualizar jerarquía de vinos:', error);
        toast.error('Vino actualizado pero hubo un problema al refrescar la vista');
      }
    },
    onError: (error) => {
      toast.error(`Error al actualizar el vino: ${error?.message || error?.toString() || 'Error desconocido'}`);
    }
  });

  // Mutation para eliminar vino
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      try {
        const result = await firebaseWinesService.delete(id);
        return result;
      } catch (error) {
        console.error('❌ Error al eliminar vino de Firebase:', error);
        return null;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseWines']);
      setDeleteDialog({ open: false, wine: null });
      toast.success('Vino eliminado exitosamente');
    },
    onError: (error) => {
      toast.error(error.message || 'Error al eliminar el vino');
      setDeleteDialog({ open: false, wine: null });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      categoryId: '',
      wineryId: '',
      type: '',
      year: '',
      alcoholContent: '',
      stock: '',
      weightKg: '',
      image: null
    });
    setEditingWine(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenDialog = (wine = null) => {
    if (wine) {
      setEditingWine(wine);
      setFormData({
        name: wine.name,
        description: wine.description,
        price: wine.price.toString(),
        categoryId: wine.categoryId,
        wineryId: wine.wineryId || '',
        type: wine.type,
        year: wine.year?.toString() || '',
        alcoholContent: wine.alcoholContent?.toString() || '',
        stock: wine.stock?.toString() || '',
        weightKg: wine.weightKg?.toString() || '',
        image: null
      });
      setImagePreview(wine.imageUrl);
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    resetForm();
    // Asegurar que el foco vuelva a un elemento seguro
    setTimeout(() => {
      const addButton = document.querySelector('[data-testid="add-wine-button"]');
      if (addButton) {
        addButton.focus();
      }
    }, 100);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('La descripción es requerida');
      return;
    }
    
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      toast.error('El precio debe ser un número válido mayor a 0');
      return;
    }
    
    if (!formData.categoryId) {
      toast.error('La categoría es requerida');
      return;
    }

    if (!formData.wineryId) {
      toast.error('La bodega es requerida');
      return;
    }
    
    if (!formData.type.trim()) {
      toast.error('El tipo es requerido');
      return;
    }

    // Crear FormData para envío
    const submitData = new FormData();
    submitData.append('name', formData.name.trim());
    submitData.append('description', formData.description.trim());
    submitData.append('price', formData.price);
    submitData.append('categoryId', formData.categoryId);
    submitData.append('wineryId', formData.wineryId);
    submitData.append('type', formData.type.trim());
    
    if (formData.year) submitData.append('year', formData.year);
    if (formData.alcoholContent) submitData.append('alcoholContent', formData.alcoholContent);
    if (formData.stock) submitData.append('stock', formData.stock);
    if (formData.weightKg) submitData.append('weightKg', formData.weightKg);
    if (formData.image) {
      submitData.append('image', formData.image);
    }

    if (editingWine) {
      updateMutation.mutate({
        id: editingWine.id,
        formData: submitData
      });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDeleteClick = (wine) => {
    setDeleteDialog({ open: true, wine });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.wine) {
      deleteMutation.mutate(deleteDialog.wine.id);
    }
  };

  const getCategoryName = (categoryId) => {
    if (!Array.isArray(categories) || categories.length === 0) {
      return 'Sin categoría';
    }
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Sin categoría';
  };

  const getWineryName = (wineryId) => {
    if (!Array.isArray(wineries) || wineries.length === 0) {
      return 'Sin bodega';
    }
    const winery = wineries.find(w => w.id === wineryId);
    return winery ? winery.name : 'Sin bodega';
  };

  const getTypeName = (typeId) => {
    if (!Array.isArray(types) || types.length === 0) {
      return 'Sin tipo';
    }
    const type = types.find(t => t.id === typeId || t.name === typeId);
    return type ? type.name : typeId || 'Sin tipo';
  };

  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'tinto': return 'error';
      case 'blanco': return 'warning';
      case 'rosado': return 'secondary';
      case 'espumoso': return 'info';
      default: return 'default';
    }
  };

  // Organizar vinos por jerarquía: Categoría -> Bodega -> Tipo -> Vino
  const organizeWinesByHierarchy = () => {
    const hierarchy = {};

    if (!Array.isArray(wines) || wines.length === 0) {
      return hierarchy;
    }

    wines.forEach(wine => {
      const categoryName = getCategoryName(wine.categoryId);
      const wineryName = getWineryName(wine.wineryId);
      const typeName = getTypeName(wine.type);

      if (!hierarchy[categoryName]) {
        hierarchy[categoryName] = {};
      }
      if (!hierarchy[categoryName][wineryName]) {
        hierarchy[categoryName][wineryName] = {};
      }
      if (!hierarchy[categoryName][wineryName][typeName]) {
        hierarchy[categoryName][wineryName][typeName] = [];
      }

      hierarchy[categoryName][wineryName][typeName].push(wine);
    });

    // Ordenar cada nivel
    Object.keys(hierarchy).forEach(category => {
      Object.keys(hierarchy[category]).forEach(winery => {
        Object.keys(hierarchy[category][winery]).forEach(type => {
          hierarchy[category][winery][type].sort((a, b) => a.name.localeCompare(b.name));
        });
      });
    });

    return hierarchy;
  };

  const wineHierarchy = organizeWinesByHierarchy();

  // Calcular vinos con bajo stock (7 unidades o menos)
  const lowStockWines = wines.filter(wine => wine.stock <= 7 && wine.stock > 0);
  const outOfStockWines = wines.filter(wine => wine.stock === 0);

  // Componente del widget de bajo stock - Diseño profesional
  const LowStockWidget = () => {
    const totalIssues = lowStockWines.length + outOfStockWines.length;
    
    if (totalIssues === 0) {
      return (
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)',
            border: 'none',
            borderRadius: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box 
                sx={{ 
                  width: 56, 
                  height: 56, 
                  borderRadius: '50%', 
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <InventoryIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                  Inventario Óptimo
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Todos los productos tienen stock suficiente
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>✓</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card 
        sx={{ 
          mb: 4, 
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Header del widget */}
        <Box 
          sx={{ 
            background: outOfStockWines.length > 0 
              ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
              : 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
            p: 3,
            color: 'white'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box 
              sx={{ 
                width: 56, 
                height: 56, 
                borderRadius: '50%', 
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WarningIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Alerta de Inventario
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.95 }}>
                {totalIssues} producto{totalIssues !== 1 ? 's' : ''} requiere{totalIssues !== 1 ? 'n' : ''} atención
              </Typography>
            </Box>
            <Box 
              sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                background: 'rgba(255, 255, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                {totalIssues}
              </Typography>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          {/* Sección de productos sin stock */}
          {outOfStockWines.length > 0 && (
            <Box sx={{ borderBottom: outOfStockWines.length > 0 && lowStockWines.length > 0 ? '1px solid #f0f0f0' : 'none' }}>
              <Box sx={{ p: 3, pb: 2, background: '#fff5f5' }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      backgroundColor: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>
                      !
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 600 }}>
                    Sin Stock ({outOfStockWines.length})
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {outOfStockWines.slice(0, 6).map((wine) => (
                    <Grid item xs={12} sm={6} key={wine.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          background: 'white',
                          border: '1px solid #fecaca',
                          borderRadius: 2,
                          '&:hover': { boxShadow: '0 4px 12px rgba(239, 68, 68, 0.1)' }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              backgroundColor: '#fef2f2',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <WineIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                              {wine.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                              {getCategoryName(wine.categoryId)} • {getWineryName(wine.wineryId)}
                            </Typography>
                          </Box>
                          <Chip 
                            label="AGOTADO" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#dc2626', 
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }} 
                          />
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {outOfStockWines.length > 6 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                      +{outOfStockWines.length - 6} productos más sin stock
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setStockDialog({ open: true, tab: 0 })}
                    sx={{
                      borderColor: '#dc2626',
                      color: '#dc2626',
                      '&:hover': {
                        borderColor: '#b91c1c',
                        backgroundColor: '#fef2f2'
                      }
                    }}
                  >
                    Ver todos los productos sin stock ({outOfStockWines.length})
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Sección de productos con bajo stock */}
          {lowStockWines.length > 0 && (
            <Box>
              <Box sx={{ p: 3, pb: 2, background: '#fffbeb' }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      backgroundColor: '#f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>
                      ⚠
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#d97706', fontWeight: 600 }}>
                    Bajo Stock ({lowStockWines.length})
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {lowStockWines.slice(0, 6).map((wine) => (
                    <Grid item xs={12} sm={6} key={wine.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          background: 'white',
                          border: '1px solid #fed7aa',
                          borderRadius: 2,
                          '&:hover': { boxShadow: '0 4px 12px rgba(245, 158, 11, 0.1)' }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              backgroundColor: '#fef3c7',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <WineIcon sx={{ color: '#d97706', fontSize: 20 }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                              {wine.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                              {getCategoryName(wine.categoryId)} • {getWineryName(wine.wineryId)}
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${wine.stock} unidades`}
                            size="small" 
                            sx={{ 
                              backgroundColor: wine.stock <= 5 ? '#f59e0b' : '#10b981', 
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }} 
                          />
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {lowStockWines.length > 6 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                      +{lowStockWines.length - 6} productos más con bajo stock
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setStockDialog({ open: true, tab: 1 })}
                    sx={{
                      borderColor: '#d97706',
                      color: '#d97706',
                      '&:hover': {
                        borderColor: '#b45309',
                        backgroundColor: '#fffbeb'
                      }
                    }}
                  >
                    Ver todos los productos con bajo stock ({lowStockWines.length})
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box>
      <BackButton />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WineIcon />
          Gestión de Vinos
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          data-testid="add-wine-button"
        >
          Nuevo Vino
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los vinos
        </Alert>
      )}

      {/* Widget de Bajo Stock */}
      <LowStockWidget />

      {/* Vista jerárquica de vinos */}
      <Box key={hierarchyKey}>
        {Object.keys(wineHierarchy).sort().map((categoryName) => (
          <Accordion key={categoryName} defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                backgroundColor: '#F5EDE4',
                '&:hover': { backgroundColor: '#E8D5C4' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 32, 
                  height: 32, 
                  backgroundColor: 'secondary.main', 
                  borderRadius: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.2rem' }}>📁</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                  {categoryName}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(wineHierarchy[categoryName]).sort().map((wineryName) => (
                <Accordion key={wineryName} sx={{ mb: 1, ml: 2 }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: '#FEFCFA',
                      border: '1px solid #E8D5C4',
                      '&:hover': { backgroundColor: '#F5EDE4' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ 
                        width: 28, 
                        height: 28, 
                        backgroundColor: '#DAA520',
                        fontSize: '0.9rem'
                      }}>
                        <WineryIcon />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {wineryName}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pl: 2 }}>
                    {Object.keys(wineHierarchy[categoryName][wineryName]).sort().map((typeName) => (
                      <Box key={typeName} sx={{ mb: 2 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mb: 1,
                          p: 1,
                          backgroundColor: '#F9F7F4',
                          borderRadius: 1,
                          border: '1px solid #DAA520'
                        }}>
                          <Chip 
                            label={`🍷 ${typeName}`}
                            color={getTypeColor(typeName)}
                            variant="filled"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {wineHierarchy[categoryName][wineryName][typeName].length} vino{wineHierarchy[categoryName][wineryName][typeName].length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ pl: 2 }}>
                          {wineHierarchy[categoryName][wineryName][typeName].map((wine) => (
                            <Box key={wine.id} sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2, 
                              p: 2, 
                              border: '2px solid #E8D5C4', 
                              borderRadius: 2, 
                              mb: 1,
                              backgroundColor: '#FEFCFA',
                              boxShadow: '0 1px 3px rgba(114, 47, 55, 0.1)',
                              transition: 'all 0.2s ease',
                              '&:hover': { 
                                backgroundColor: '#F5EDE4',
                                borderColor: '#DAA520',
                                boxShadow: '0 2px 8px rgba(114, 47, 55, 0.15)',
                                transform: 'translateY(-1px)'
                              }
                            }}>
                              <Avatar
                                src={wine.imageUrl}
                                variant="rounded"
                                sx={{ 
                                  width: 60, 
                                  height: 60,
                                  border: '2px solid #DAA520',
                                  backgroundColor: '#722F37'
                                }}
                              >
                                <WineIcon sx={{ color: '#E8D5C4', fontSize: '1.5rem' }} />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'secondary.main' }}>
                                    {wine.name}
                                  </Typography>
                                  {wine.year && (
                                    <Chip 
                                      label={wine.year}
                                      size="small"
                                      sx={{ 
                                        backgroundColor: '#DAA520', 
                                        color: 'white',
                                        fontWeight: 'bold',
                                        height: 20
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    mb: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }}
                                >
                                  {wine.description}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#722F37' }}>
                                    €{wine.price?.toFixed(2)}
                                  </Typography>
                                  <Chip 
                                    label={`📦 ${wine.stock || 0} unidades`}
                                    color={wine.stock > 10 ? 'success' : wine.stock > 0 ? 'warning' : 'error'}
                                    variant="filled"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                  {wine.alcoholContent && (
                                    <Chip 
                                      label={`🍇 ${wine.alcoholContent}%`}
                                      variant="outlined"
                                      size="small"
                                      sx={{ 
                                        borderColor: '#DAA520',
                                        color: '#722F37',
                                        fontWeight: 500
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 1,
                                alignItems: 'center'
                              }}>
                                <Tooltip title="Editar Vino" arrow>
                                  <IconButton
                                    onClick={() => handleOpenDialog(wine)}
                                    sx={{
                                      backgroundColor: '#E8D5C4',
                                      color: '#722F37',
                                      '&:hover': {
                                        backgroundColor: '#DAA520',
                                        color: 'white'
                                      }
                                    }}
                                    size="small"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar Vino" arrow>
                                  <IconButton
                                    onClick={() => handleDeleteClick(wine)}
                                    sx={{
                                      backgroundColor: '#ffebee',
                                      color: '#d32f2f',
                                      '&:hover': {
                                        backgroundColor: '#d32f2f',
                                        color: 'white'
                                      }
                                    }}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        {typeName !== Object.keys(wineHierarchy[categoryName][wineryName])[Object.keys(wineHierarchy[categoryName][wineryName]).length - 1] && (
                          <Divider sx={{ my: 1 }} />
                        )}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {wines.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No hay vinos registrados
          </Typography>
        </Box>
      )}

      {/* Dialog para crear/editar vino */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        disablePortal={false}
        keepMounted={false}
        aria-labelledby="wine-dialog-title"
        aria-describedby="wine-dialog-description"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle id="wine-dialog-title">
            {editingWine ? 'Editar Vino' : 'Nuevo Vino'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Imagen */}
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="subtitle2" gutterBottom>
                    Imagen del Vino
                  </Typography>
                  <Box
                    sx={{
                      width: 150,
                      height: 200,
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      mx: 'auto',
                      mb: 2,
                      backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {!imagePreview && <PhotoIcon sx={{ fontSize: 40, color: '#ccc' }} />}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Cambiar Imagen
                  </Button>
                </Box>
              </Grid>

              {/* Información básica */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      label="Nombre del Vino"
                      fullWidth
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      inputProps={{
                        'aria-label': 'Nombre del vino'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Descripción"
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Precio"
                      type="number"
                      fullWidth
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><EuroIcon /></InputAdornment>,
                        inputProps: { min: 0, step: 0.01 }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Categoría</InputLabel>
                      <Select
                        value={formData.categoryId}
                        label="Categoría"
                        onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                      >
                        {Array.isArray(categories) && categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Bodega</InputLabel>
                      <Select
                        value={formData.wineryId}
                        label="Bodega"
                        onChange={(e) => setFormData({ ...formData, wineryId: e.target.value })}
                      >
                        {Array.isArray(wineries) && wineries
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((winery) => (
                          <MenuItem key={winery.id} value={winery.id}>
                            {winery.name} - {winery.region}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={formData.type}
                        label="Tipo"
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      >
                        {Array.isArray(types) && types.map((type) => (
                          <MenuItem key={type.id} value={type.name}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Añada"
                      type="number"
                      fullWidth
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      inputProps={{ min: 1800, max: new Date().getFullYear() }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Graduación Alcohólica (%)"
                      type="number"
                      fullWidth
                      value={formData.alcoholContent}
                      onChange={(e) => setFormData({ ...formData, alcoholContent: e.target.value })}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Stock"
                      type="number"
                      fullWidth
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Peso por botella (kg)"
                      type="number"
                      fullWidth
                      value={formData.weightKg}
                      onChange={(e) => setFormData({ ...formData, weightKg: e.target.value })}
                      inputProps={{ min: 0, step: 0.1 }}
                      helperText="Para calcular envío. Por defecto 1 kg si se deja vacío."
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              color="secondary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingWine ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, wine: null })}
        disablePortal={false}
        keepMounted={false}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            ¿Estás seguro de que deseas eliminar el vino "{deleteDialog.wine?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, wine: null })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para ver lista completa de vinos con problemas de stock */}
      <Dialog 
        open={stockDialog.open} 
        onClose={() => setStockDialog({ open: false, tab: 0 })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <WarningIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                Gestión de Inventario
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                Lista completa de productos que requieren atención
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={stockDialog.tab} 
            onChange={(e, newValue) => setStockDialog(prev => ({ ...prev, tab: newValue }))}
            sx={{ px: 3 }}
          >
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge badgeContent={outOfStockWines.length} color="error">
                    <Typography>Sin Stock</Typography>
                  </Badge>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge badgeContent={lowStockWines.length} color="warning">
                    <Typography>Bajo Stock</Typography>
                  </Badge>
                </Box>
              } 
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Tab de productos sin stock */}
          {stockDialog.tab === 0 && (
            <Box>
              {outOfStockWines.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <InventoryIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#15803d', mb: 1 }}>
                    ¡Excelente! No hay productos sin stock
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Todos los productos tienen inventario disponible
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 3, p: 3, background: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          backgroundColor: '#dc2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography sx={{ color: 'white', fontWeight: 'bold' }}>!</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 600 }}>
                          {outOfStockWines.length} producto{outOfStockWines.length !== 1 ? 's' : ''} sin stock
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                          Estos productos no están disponibles para la venta
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Grid container spacing={2}>
                    {outOfStockWines.map((wine) => (
                      <Grid item xs={12} sm={6} md={4} key={wine.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            p: 2.5, 
                            border: '1px solid #fecaca',
                            borderRadius: 2,
                            '&:hover': { 
                              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.3s ease'
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Box 
                              sx={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: '50%', 
                                backgroundColor: '#fef2f2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <WineIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                            </Box>
                            <Box flex={1}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                                {wine.name}
                              </Typography>
                              <Chip 
                                label="AGOTADO" 
                                size="small" 
                                sx={{ 
                                  backgroundColor: '#dc2626', 
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  mt: 0.5
                                }} 
                              />
                            </Box>
                          </Box>
                          
                          <Divider sx={{ my: 1.5 }} />
                          
                          <Box>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                              <strong>Categoría:</strong> {getCategoryName(wine.categoryId)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                              <strong>Bodega:</strong> {getWineryName(wine.wineryId)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                              <strong>Precio:</strong> €{wine.price}
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setFormData({
                                  name: wine.name,
                                  description: wine.description,
                                  price: wine.price,
                                  categoryId: wine.categoryId,
                                  wineryId: wine.wineryId,
                                  type: wine.type,
                                  year: wine.year || '',
                                  alcoholContent: wine.alcoholContent || '',
                                  stock: wine.stock,
                                  image: null
                                });
                                setEditingWine(wine);
                                setStockDialog({ open: false, tab: 0 });
                                setOpen(true);
                              }}
                              startIcon={<EditIcon />}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Actualizar Stock
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Tab de productos con bajo stock */}
          {stockDialog.tab === 1 && (
            <Box>
              {lowStockWines.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <InventoryIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#15803d', mb: 1 }}>
                    ¡Perfecto! No hay productos con bajo stock
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Todos los productos tienen stock suficiente
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 3, p: 3, background: '#fffbeb', borderRadius: 2, border: '1px solid #fed7aa' }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          backgroundColor: '#f59e0b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography sx={{ color: 'white', fontWeight: 'bold' }}>⚠</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ color: '#d97706', fontWeight: 600 }}>
                          {lowStockWines.length} producto{lowStockWines.length !== 1 ? 's' : ''} con bajo stock
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#92400e' }}>
                          Considera reponer estos productos pronto
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Grid container spacing={2}>
                    {lowStockWines.map((wine) => (
                      <Grid item xs={12} sm={6} md={4} key={wine.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            p: 2.5, 
                            border: '1px solid #fed7aa',
                            borderRadius: 2,
                            '&:hover': { 
                              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.3s ease'
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Box 
                              sx={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: '50%', 
                                backgroundColor: '#fef3c7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <WineIcon sx={{ color: '#d97706', fontSize: 24 }} />
                            </Box>
                            <Box flex={1}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                                {wine.name}
                              </Typography>
                              <Chip 
                                label={`${wine.stock} unidades`}
                                size="small" 
                                sx={{ 
                                  backgroundColor: wine.stock <= 5 ? '#f59e0b' : '#10b981', 
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  mt: 0.5
                                }} 
                              />
                            </Box>
                          </Box>
                          
                          <Divider sx={{ my: 1.5 }} />
                          
                          <Box>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                              <strong>Categoría:</strong> {getCategoryName(wine.categoryId)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                              <strong>Bodega:</strong> {getWineryName(wine.wineryId)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                              <strong>Precio:</strong> €{wine.price}
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              onClick={() => {
                                setFormData({
                                  name: wine.name,
                                  description: wine.description,
                                  price: wine.price,
                                  categoryId: wine.categoryId,
                                  wineryId: wine.wineryId,
                                  type: wine.type,
                                  year: wine.year || '',
                                  alcoholContent: wine.alcoholContent || '',
                                  stock: wine.stock,
                                  image: null
                                });
                                setEditingWine(wine);
                                setStockDialog({ open: false, tab: 0 });
                                setOpen(true);
                              }}
                              startIcon={<EditIcon />}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Actualizar Stock
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button 
            onClick={() => setStockDialog({ open: false, tab: 0 })}
            color="inherit"
            size="large"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWines; 